import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, usePermissions } from '@/features/auth';
import type { PermissionKey } from '@/constants/permissions';
import { LoadingSpinner } from './utils';

const FORCE_PASSWORD_PATH = '/change-password';

export const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname } = useLocation();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.must_change_password && pathname !== FORCE_PASSWORD_PATH) {
    return <Navigate to={`${FORCE_PASSWORD_PATH}?forced=1`} replace />;
  }
  return children;
};

export const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  return !isAuthenticated ? children : <Navigate to="/projects/my?page=1" />;
};

export const RootRedirect: React.FC<{ guest: React.ReactElement }> = ({ guest }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  return isAuthenticated ? <Navigate to="/projects/my?page=1" replace /> : guest;
};

interface PermissionRouteProps {
  permission: PermissionKey;
  fallback?: string;
  children: React.ReactElement;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  permission,
  fallback = '/projects/my',
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const perms = usePermissions();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!perms.has(permission)) return <Navigate to={fallback} replace />;
  return children;
};

export const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!(user?.is_staff || user?.is_superuser)) return <Navigate to="/dashboard" replace />;
  return children;
};

export const SuperadminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.is_superuser) return <Navigate to="/admin/keys" replace />;
  return children;
};

export const StaffRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!(user?.is_staff || user?.is_superuser)) return <Navigate to="/dashboard" replace />;
  return children;
};
